import { computed, inject, Injectable } from '@angular/core';
import { AUTH_SESSION } from '@shared/di/auth-session.token';
import {
  ADMIN,
  AUTHORITY_PRIORITY,
  MEMBER,
  PRACTITIONER,
  SUPER_ADMIN,
} from '../constants/auth';

@Injectable({ providedIn: 'root' })
export class RoleService {
  /**
   * Public so a consumer can tell "roles not loaded yet" (empty) from "loaded and
   * none matched" — the booleans below cannot express that difference, and
   * treating empty as "denied" flashes a denial screen during login.
   */
  readonly authorityNames = inject(AUTH_SESSION).authorityNames;

  readonly isSuperAdmin = computed(() => this.authorityNames().includes(SUPER_ADMIN));
  readonly isAdmin = computed(() => this.authorityNames().includes(ADMIN));
  readonly isPractitioner = computed(() => this.authorityNames().includes(PRACTITIONER));
  readonly isMember = computed(() => this.authorityNames().includes(MEMBER));
  /**
   * Whether this account can work in the web platform at all.
   *
   * Defined here, not in the component that blocks: the layout has to hide the
   * sidebar on the same condition, and two copies of `isPractitioner() ||
   * isSuperAdmin()` would eventually disagree.
   *
   * False while the authorities are still loading — check `authorityNames()`
   * before treating it as a denial.
   */
  readonly canAccessPlatform = computed(() => this.isPractitioner() || this.isSuperAdmin());

  readonly primaryRole = computed(
    () => AUTHORITY_PRIORITY.find((role) => this.authorityNames().includes(role)) ?? null,
  );

  hasRole(role: string): boolean {
    return this.authorityNames().includes(role);
  }
}
